import React, { useEffect, useMemo, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { createRoot } from "react-dom/client";
import "maplibre-gl/dist/maplibre-gl.css";
import { T } from "../../app/tokens.js";
import { log } from "../../utils/logger.js";
import {
  SPACE_FIT_HEX,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
} from "../../utils/spaceFitVerdict.js";

maplibregl.setWorkerUrl(workerUrl);

export const OPENFREEMAP_STYLE_URL = "https://tiles.openfreemap.org/styles/positron";
export const OPENFREEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org/" target="_blank" rel="noopener">OpenFreeMap</a> · ' +
  '<a href="https://openmaptiles.org/" target="_blank" rel="noopener">© OpenMapTiles</a> · ' +
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap contributors</a>';

export function toCoord(v) {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function effectiveLat(loc) {
  const geoLat = toCoord(loc.geoLat);
  return geoLat != null ? geoLat : toCoord(loc.lat);
}

export function effectiveLng(loc) {
  const geoLng = toCoord(loc.geoLng);
  return geoLng != null ? geoLng : toCoord(loc.lng);
}

export function hasCoords(loc) {
  return effectiveLat(loc) != null && effectiveLng(loc) != null;
}

// The detail card needs a trustworthy building pin. Keep the portfolio's
// historical coordinate filtering unchanged; this stricter check is detail-only.
export function preciseOfficePosition(loc) {
  const lat = effectiveLat(loc);
  const lng = effectiveLng(loc);
  if (
    lat == null ||
    lng == null ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180 ||
    (lat === 0 && lng === 0) ||
    (loc.geoLat != null && ["GEOMETRIC_CENTER", "APPROXIMATE"].includes(loc.geoAccuracy))
  ) {
    return null;
  }
  return [lat, lng];
}

function featureCollection(features) {
  return {
    type: "FeatureCollection",
    features: features.map(({ key, coordinates, locations, color, severity }) => ({
      type: "Feature",
      properties: { featureKey: key, count: locations.length, color, severity },
      geometry: { type: "Point", coordinates: [coordinates[1], coordinates[0]] },
    })),
  };
}

const severityColor = [
  "match",
  ["get", "minSeverity"],
  0, SPACE_FIT_HEX[SPACE_FIT_VERDICT_OVER],
  1, SPACE_FIT_HEX[SPACE_FIT_VERDICT_DENSE],
  2, SPACE_FIT_HEX[SPACE_FIT_VERDICT_UNDER],
  3, SPACE_FIT_HEX[SPACE_FIT_VERDICT_RIGHT],
  4, T.buildBlue,
  T.mid,
];

/**
 * MapLibre-backed shared map surface. Office points are GeoJSON so clusters
 * remain responsive and use MapLibre's worker-based supercluster implementation.
 * Singleton markers are DOM markers to preserve crisp token-styled pins and
 * stable marker test IDs; co-located and cluster count badges are map layers.
 */
export function OfficeMapCanvas({
  features,
  mapRef,
  center = [20, 10],
  zoom = 2,
  cluster = true,
  maxZoom = 19,
  onSelectLocation,
  onSelectFeature,
  renderPopup,
  className,
  style,
  ariaLabel,
  testId = "office-map",
}) {
  const containerRef = useRef(null);
  const markersRef = useRef(new Map());
  const recordsRef = useRef(new Map());
  const latestRef = useRef({});
  const collection = useMemo(() => featureCollection(features), [features]);
  const [mapError, setMapError] = React.useState(null);
  const centerLat = center[0];
  const centerLon = center[1];
  const centerLngLat = [centerLon, centerLat];

  latestRef.current = { onSelectLocation, onSelectFeature, renderPopup, features, collection };
  recordsRef.current = new Map(features.map((feature) => [feature.key, feature]));

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: OPENFREEMAP_STYLE_URL,
        center: centerLngLat,
        zoom,
        maxZoom,
        attributionControl: false,
      });
    } catch (error) {
      if (
        error?.name === "GPUInitializationError" ||
        /WebGL2 is required/i.test(String(error?.message))
      ) {
        setMapError("This browser needs WebGL 2 to display the map.");
        return undefined;
      }
      throw error;
    }
    if (mapRef) mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(
      new maplibregl.AttributionControl({
        compact: false,
        customAttribution: OPENFREEMAP_ATTRIBUTION,
      }),
      "bottom-right",
    );

    let popup = null;
    const removeMarkers = () => {
      for (const { marker } of markersRef.current.values()) marker.remove();
      markersRef.current.clear();
    };

    const openPopup = (feature, lngLat) => {
      // Record-grain maps can navigate directly; office callers keep popups.
      if (latestRef.current.onSelectFeature) {
        latestRef.current.onSelectFeature(feature);
        return;
      }
      const content = latestRef.current.renderPopup?.(feature);
      if (!content) return;
      popup?.remove();
      const popupElement = document.createElement("div");
      const popupRoot = createRoot(popupElement);
      popupRoot.render(content);
      popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, maxWidth: "280px" })
        .setLngLat(lngLat)
        .setDOMContent(popupElement)
        .addTo(map);
      popup.on("close", () => popupRoot.unmount());
    };

    const addMapLayers = () => {
      if (map.getSource("offices")) return;
      map.addSource("offices", {
        type: "geojson",
        data: latestRef.current.collection,
        cluster,
        clusterRadius: 40,
        clusterMaxZoom: 14,
      ...(cluster ? { clusterProperties: { minSeverity: ["min", ["get", "severity"]] } } : {}),
      });
      if (cluster) {
        map.addLayer({
          id: "office-cluster-circles",
          type: "circle",
          source: "offices",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": severityColor,
            "circle-radius": 18,
            "circle-stroke-color": "rgba(255,255,255,0.9)",
            "circle-stroke-width": 2.5,
            "circle-stroke-opacity": 1,
            "circle-opacity": 0.98,
          },
        });
        map.addLayer({
          id: "office-cluster-count",
          type: "symbol",
          source: "offices",
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["Noto Sans Bold"],
            "text-size": 13,
            "text-allow-overlap": true,
          },
          paint: { "text-color": T.white },
        });
      }
      const syncSingletonMarkers = () => {
        let sourceFeatures = [];
        if (cluster) {
          try {
            sourceFeatures = map.querySourceFeatures("offices");
          } catch {
            return;
          }
        } else {
          // MapLibre does not load a GeoJSON source that has no referencing
          // layer. The compact detail map has no cluster layers, so create
          // its single pin directly from the supplied records.
          sourceFeatures = latestRef.current.features.map(({ key }) => ({
            properties: { featureKey: key },
          }));
        }
        const visibleKeys = new Set();
        for (const sourceFeature of sourceFeatures) {
          const properties = sourceFeature.properties ?? {};
          if (properties.cluster || properties.point_count || !properties.featureKey) continue;
          const record = recordsRef.current.get(properties.featureKey);
          if (!record) continue;
          visibleKeys.add(record.key);
          const signature = JSON.stringify([
            record.coordinates,
            record.color,
            record.locations.map(({ id }) => id),
            record.locations[0]?.market || record.locations[0]?.id,
          ]);
          const existing = markersRef.current.get(record.key);
          if (existing?.signature === signature) continue;
          existing?.marker.remove();
          const count = record.locations.length;
          const size = count > 1 ? 28 : 16;
          const element = document.createElement("button");
          element.type = "button";
          element.setAttribute("aria-label", count > 1
            ? `${count} locations at this address`
            : (record.locations[0].market || record.locations[0].id));
          element.dataset.testid = count > 1
            ? `colocated-marker-${record.key.replace(/^coords:/, "")}`
            : `map-marker-${record.locations[0].id}`;
          element.className = "office-map-marker";
          Object.assign(element.style, {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: "50%",
            background: record.color,
            color: T.white,
            fontSize: count > 1 ? "11px" : "0",
            fontWeight: "700",
            border: `2.5px solid ${T.white}`,
            boxSizing: "border-box",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
            opacity: "0.9",
            padding: "0",
            cursor: "pointer",
          });
          if (count > 1) element.textContent = String(count);
          const marker = new maplibregl.Marker({ element, anchor: "center" })
            .setLngLat([record.coordinates[1], record.coordinates[0]])
            .addTo(map);
          element.addEventListener("mouseenter", () => {
            element.style.width = "24px";
            element.style.height = "24px";
            element.style.opacity = "1";
          });
          element.addEventListener("mouseleave", () => {
            element.style.width = `${size}px`;
            element.style.height = `${size}px`;
            element.style.opacity = "0.9";
          });
          element.addEventListener("click", (event) => {
            event.stopPropagation();
            const currentRecord = recordsRef.current.get(record.key);
            if (!currentRecord) return;
            openPopup(currentRecord, [
              currentRecord.coordinates[1],
              currentRecord.coordinates[0],
            ]);
          });
          markersRef.current.set(record.key, { marker, signature });
        }
        for (const [key, value] of markersRef.current) {
          if (!visibleKeys.has(key)) {
            value.marker.remove();
            markersRef.current.delete(key);
          }
        }
      };

      map.on("render", syncSingletonMarkers);
      if (cluster) {
        map.on("click", "office-cluster-circles", async (event) => {
          const feature = map.queryRenderedFeatures(
            event.point,
            { layers: ["office-cluster-circles"] },
          )[0];
          if (!feature) return;
          const source = map.getSource("offices");
          if (!source) return;
          try {
            const nextZoom = await source.getClusterExpansionZoom(feature.properties.cluster_id);
            map.easeTo({ center: feature.geometry.coordinates, zoom: nextZoom });
          } catch (error) {
            log.warn("Office map cluster expansion failed", { error: String(error) });
          }
        });
        map.on("mouseenter", "office-cluster-circles", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "office-cluster-circles", () => { map.getCanvas().style.cursor = ""; });
      }
      syncSingletonMarkers();
    };

    map.on("load", addMapLayers);
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => map.resize())
      : null;
    if (containerRef.current) resizeObserver?.observe(containerRef.current);

    return () => {
      resizeObserver?.disconnect();
      popup?.remove();
      removeMarkers();
      map.remove();
      if (mapRef?.current === map) mapRef.current = null;
    };
    // The map instance is deliberately stable for the component lifetime.
    // Feature and camera changes are applied by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;
    const source = map.getSource("offices");
    if (source) source.setData(collection);
  }, [collection, mapRef]);

  useEffect(() => {
    const map = mapRef?.current;
    if (map) map.jumpTo({ center: [centerLon, centerLat], zoom });
  }, [centerLon, centerLat, zoom, mapRef]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: "100%", height: "100%", position: "relative", ...style }}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {mapError && (
        <div
          role="status"
          data-testid="office-map-webgl-unavailable"
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            padding: 24,
            textAlign: "center",
            background: T.card,
            color: T.mid,
            fontSize: T.fsSm,
          }}
        >
          {mapError}
        </div>
      )}
    </div>
  );
}

export function severityColorForFeature(feature) {
  const verdictBySeverity = [
    SPACE_FIT_VERDICT_OVER,
    SPACE_FIT_VERDICT_DENSE,
    SPACE_FIT_VERDICT_UNDER,
    SPACE_FIT_VERDICT_RIGHT,
  ];
  if (feature.severity <= 3) return SPACE_FIT_HEX[verdictBySeverity[feature.severity]];
  return feature.color ?? T.mid;
}